'use client';
import { type ActivityType } from '@streetferret/api';
import { type Expression } from 'mapbox-gl';
import { useMemo } from 'react';
import {
  Layer,
  type FillLayer,
  type LineLayer,
  type SymbolLayer,
} from 'react-map-gl';
import { useMapTheme } from '../../context';

interface BoundaryLayersProps {
  activityType: ActivityType;
  hoveredCityId: number | null;
  selectedCities:
    | {
        cityId: number;
        name: string;
      }[]
    | null;
  /**
   * if provided, only show bounds for these cities
   */
  cityIncludeFilter?: number[];
  /**
   * if provided, exclude these cities
   */
  cityExcludeFilter?: number[];
  hidden?: boolean;
  /**
   * The ID of the tile source to use for these layers
   */
  sourceId: string;
}
export function BoundaryLayers({
  activityType,
  cityIncludeFilter,
  cityExcludeFilter,
  selectedCities,
  hoveredCityId,
  hidden,
  sourceId,
}: BoundaryLayersProps) {
  const { theme, baseTheme } = useMapTheme();
  const isDark = baseTheme === 'dark';
  const visibility = useMemo(() => (hidden ? 'none' : 'visible'), [hidden]);

  const filterShowCities = useMemo(() => {
    return [
      'all',
      cityIncludeFilter?.length
        ? ['in', ['get', 'cityID'], ['literal', cityIncludeFilter]]
        : true,
      ['!', ['in', ['get', 'cityID'], ['literal', cityExcludeFilter ?? []]]],
    ];
  }, [cityIncludeFilter, cityExcludeFilter]);

  const boundsLayer: LineLayer = useMemo(() => {
    return {
      id: 'city-bounds-outline',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'line',
      layout: {
        visibility,
      },
      paint: {
        'line-color': theme.selected_bounds,
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          isDark ? 0.5 : 1.5, // At zoom level 9, line width is 1.5
          11,
          2, // At zoom level 11, line width is 2
        ],
      },
      minzoom: 7,
      filter: filterShowCities,
    };
  }, [isDark, theme, filterShowCities, visibility, sourceId]);
  const boundaryLabel: SymbolLayer = useMemo(() => {
    return {
      id: 'boundary-label',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'symbol',
      layout: {
        visibility,
        'text-font': ['Boundary-Label'],
        'text-max-angle': 15,
        'text-field': cityNameExpr(selectedCities),
        'symbol-placement': 'line',
        'text-anchor': 'center',
        'text-transform': 'uppercase',
        'text-keep-upright': true,
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          10, // Smaller text at lower zoom levels
          14,
          14, // Larger text at higher zoom levels
        ],
        'text-offset': [0, 0.8], // Offset text inside the line
      },
      paint: {
        'text-color': isDark ? '#FFFFFF' : '#111111',
      },
      filter: selectedCitiesFilter(selectedCities),
    };
  }, [isDark, selectedCities, visibility, sourceId]);

  const boundsFillLayer: FillLayer = useMemo(() => {
    return {
      id: 'city-bounds-fill',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'fill',
      minzoom: 1,
      maxzoom: 9,
      layout: {
        visibility,
      },
      paint: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['get', activityType == 'run' ? 'runComplete' : 'cycComplete'],
          0,
          theme.bounds_progress_min,
          0.5,
          theme.bounds_progress_mid,
          1,
          theme.bounds_progress_max,
        ],
      },
      filter: selectedCities
        ? ['all', filterShowCities, selectedCitiesFilter(selectedCities)]
        : filterShowCities,
    };
  }, [
    sourceId,
    visibility,
    activityType,
    theme,
    selectedCities,
    filterShowCities,
  ]);
  const boundsClickLayer: FillLayer = useMemo(() => {
    return {
      id: 'city-bounds-fill-click',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'fill',
      layout: {
        visibility,
      },
      paint: {
        'fill-color': 'rgba(255, 255, 255, 0.0)',
      },
      filter: filterShowCities,
    };
  }, [filterShowCities, visibility, sourceId]);
  const boundsHover: FillLayer = useMemo(() => {
    return {
      id: 'city-bounds-hover',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'fill',
      maxzoom: 13,
      layout: {
        visibility,
      },
      paint: {
        'fill-color': theme.hover,
      },
      filter: [
        'all',
        ['==', ['get', 'cityID'], hoveredCityId ?? -1],
        // Don't highlight selected cities
        ['!', selectedCitiesFilter(selectedCities)],
        filterShowCities,
      ],
    };
  }, [
    theme,
    hoveredCityId,
    selectedCities,
    filterShowCities,
    visibility,
    sourceId,
  ]);
  const selectedBoundsLayer: LineLayer = useMemo(() => {
    return {
      id: 'selected-bounds-layer',
      source: sourceId,
      'source-layer': 'boundary',
      type: 'line',
      layout: {
        visibility,
      },
      paint: {
        'line-color': theme.hover,
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          3, // At zoom level 9,
          11,
          4, // At zoom level 11,
        ],
      },
      filter: selectedCitiesFilter(selectedCities),
    };
  }, [selectedCities, theme, visibility, sourceId]);

  return (
    <>
      <Layer beforeId="_background_underlay" {...boundsClickLayer} />
      <Layer beforeId="_background_underlay" {...boundsFillLayer} />
      <Layer beforeId="_background_underlay" {...boundsHover} />
      <Layer beforeId="_background_underlay" {...selectedBoundsLayer} />
      <Layer beforeId="_road_overlays" {...boundsLayer} />
      <Layer beforeId="_road_overlays" {...boundaryLabel} />
    </>
  );
}

function selectedCitiesFilter(
  selectedCities: { cityId: number; name: string }[] | null,
): Expression {
  return [
    'in',
    ['get', 'cityID'],
    ['literal', selectedCities?.map(city => city.cityId) ?? []],
  ];
}

function cityNameExpr(
  selectedCities?: { cityId: number; name: string }[] | null,
): Expression {
  if (!selectedCities || selectedCities.length === 0) {
    return ['literal', '']; // Return default if no cities are provided
  }

  const caseBlock: Expression = ['case'];

  selectedCities.forEach(city => {
    caseBlock.push(['==', ['get', 'cityID'], city.cityId], city.name);
  });

  caseBlock.push(''); // Fallback string if no match
  return caseBlock;
}
